import { Device, Study, StudyFull } from '../models';
import {
  ApiEndpoints,
  Endpoint,
  Study as StudyEntity,
  StudyDownloadPackageCreateRequest,
  StudyFull as StudyFullEntity,
  SubjectDownloadPackageCreateRequest,
} from 'adc-sirius-module-common';
import { urls } from '../utilities/dataHelpers';
import {
  mapDeviceEntityToDevice,
  mapSiteEntityToSite,
  mapStudyEntityToStudy,
  mapStudyToStudyEntity,
  mapSubjectEntityToSubject,
} from '../utilities/mappers';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';
import { DOWNLOAD_CONFIG, DOWNLOAD_JOB_CONFIG, JobStatus } from '../constants';
import i18n from '../utilities/i18n';

const handleGetStudiesByOrganization = (
  request: MediatorRequestWithResponse<Study[]>,
) => {
  const state = request.state;

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity[]>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/organization/${request.organizationKey}`,
        queryString: state ? `?row_state=${state}` : '',
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(
            (result.data ?? []).map((studyEntity) =>
              mapStudyEntityToStudy(studyEntity),
            ),
          );
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching studies:', err);
        reject(err);
      });
  });
};

const handleGetStudyByKey = (request: MediatorRequestWithResponse<Study>) => {
  request.response = new Promise((resolve, reject) => {
    const studyKey = request.key;

    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/${studyKey}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching study by ID:', err);
        reject(err);
      });
  });
};

const handleCreateStudy = (
  request: MediatorRequestWithResponse<Partial<Study>>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'POST',
        url: urls.api + 'studies',
        data: mapStudyToStudyEntity(request.data),
      })
      .then((result) => {
        if (result.status === 201) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error creating study:', err);
        reject(err);
      });
  });
};

const handleUpdateStudy = (
  request: MediatorRequestWithResponse<Partial<Study>>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'PUT',
        url: `${urls.api}studies`,
        data: mapStudyToStudyEntity(request.data),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error updating study:', err);
        reject(err);
      });
  });
};

const handleGetStudyFullByKey = (
  request: MediatorRequestWithResponse<StudyFull>,
) => {
  request.response = new Promise((resolve, reject) => {
    const studyKey = request.key;

    mediator
      .request<StudyFullEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/${studyKey}/full`,
      })
      .then((result) => {
        if (result.status === 200) {
          const subjects = result.data.subjects ?? [];
          const sites = result.data.sites ?? [];
          const devicesBySubject = result.data.devices ?? {};
          const devices = new Map<string, Device[]>();

          Object.entries(devicesBySubject).forEach(
            ([subjectKey, subjectDevices]) => {
              devices.set(
                subjectKey,
                subjectDevices.map(mapDeviceEntityToDevice),
              );
            },
          );

          subjects.forEach(({ key }) => {
            if (!devices.has(key)) {
              devices.set(key, []);
            }
          });

          resolve({
            study: mapStudyEntityToStudy(result.data.study),
            sites: sites.map(mapSiteEntityToSite),
            subjects: subjects.map(mapSubjectEntityToSubject),
            devices,
          });
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching full study by key:', err);
        reject(err);
      });
  });
};

type DownloadDataOptions = {
  createJobEndpoint: Endpoint;
  jobStatusEndpoint: Endpoint;
  downloadEndpoint: Endpoint;
  createJobPayload:
    | StudyDownloadPackageCreateRequest
    | SubjectDownloadPackageCreateRequest;
  onProgress?: (progressPercent: number) => void;
  onReconnecting?: (reconnecting: boolean) => void;
  onJobCreated?: (jobKey: string) => void;
  onAcquiring?: () => void;
  signal?: AbortSignal;
};

type DownloadJob = {
  jobKey: string;
  statusUrl: string;
  downloadUrl: string;
};

export type DownloadPackageResult = {
  blob: Blob;
  checksumSha256?: string;
};

type DownloadErrorResponse = {
  status?: number;
};

const createDownloadError = (response?: DownloadErrorResponse) => {
  switch (response?.status) {
    case 401:
      return new Error(i18n.t('Modal_Download.error_unauthorized'));
    case 403:
      return new Error(i18n.t('Modal_Download.error_forbidden'));
    case 400:
      return new Error(i18n.t('Modal_Download.error_invalid_request'));
    case 404:
      return new Error(i18n.t('Modal_Download.error_not_found'));
    case 409:
      return new Error(i18n.t('Modal_Download.error_conflict'));
    case 429:
      return new Error(i18n.t('Modal_Download.error_rate_limit'));
    case 500:
      return new Error(i18n.t('Modal_Download.error_server'));
    default:
      return new Error(i18n.t('Modal_Download.error_generic_failed'));
  }
};

const checkPollingCancelled = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new DOMException('Export status polling cancelled', 'AbortError');
  }
};

const waitForNextStatus = (delayMs: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    checkPollingCancelled(signal);
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', cancel);
      resolve();
    }, delayMs);
    const cancel = () => {
      clearTimeout(timer);
      reject(new DOMException('Export status polling cancelled', 'AbortError'));
    };

    signal?.addEventListener('abort', cancel, { once: true });
  });

const isTransientStatusError = (status?: number) =>
  status === undefined || [408, 429, 500, 502, 503, 504, 998].includes(status);

const pollDownloadJob = async (
  job: DownloadJob,
  onProgress?: DownloadDataOptions['onProgress'],
  onReconnecting?: DownloadDataOptions['onReconnecting'],
  signal?: AbortSignal,
  onJobCreated?: DownloadDataOptions['onJobCreated'],
  onAcquiring?: DownloadDataOptions['onAcquiring'],
): Promise<DownloadPackageResult> => {
  let failureDeadline: number | undefined;
  let packagingDeadline = Date.now() + DOWNLOAD_JOB_CONFIG.PACKAGING_TIMEOUT_MS;
  let highestProgressPercent = 0;

  checkPollingCancelled(signal);
  onJobCreated?.(job.jobKey);

  const retryStatus = async (requestStartedAt: number) => {
    // Include the failed request's duration, not just the gaps between retries.
    failureDeadline ??=
      requestStartedAt + DOWNLOAD_JOB_CONFIG.STATUS_TIMEOUT_MS;
    const remainingMs =
      Math.min(failureDeadline, packagingDeadline) - Date.now();

    if (remainingMs <= 0) {
      throw createDownloadError();
    }

    onReconnecting?.(true);
    await waitForNextStatus(
      Math.min(DOWNLOAD_JOB_CONFIG.POLL_INTERVAL_MS, remainingMs),
      signal,
    );
  };

  // Only this idempotent GET is retried. The independent server worker is not
  // cancelled by a browser/network failure, so never recreate its export here.
  while (true) {
    checkPollingCancelled(signal);
    const requestStartedAt = Date.now();
    const statusTimeoutMs =
      failureDeadline === undefined
        ? DOWNLOAD_JOB_CONFIG.STATUS_TIMEOUT_MS
        : failureDeadline - requestStartedAt;
    const timeoutMs = Math.min(
      statusTimeoutMs,
      packagingDeadline - requestStartedAt,
    );

    if (timeoutMs <= 0) {
      throw createDownloadError();
    }

    let statusResponse;

    try {
      statusResponse = await mediator.request<{
        jobKey: string;
        status: JobStatus;
        progressPercent: number;
        failureReason?: string;
        checksumSha256?: string;
      }>('api:invoke', {
        method: 'GET',
        url: job.statusUrl,
        timeoutMs,
      });
    } catch (error) {
      checkPollingCancelled(signal);
      const response = error as DownloadErrorResponse | undefined;

      if (!isTransientStatusError(response?.status)) {
        throw createDownloadError(response);
      }

      await retryStatus(requestStartedAt);
      continue;
    }

    checkPollingCancelled(signal);

    if (statusResponse?.status !== 200) {
      if (!isTransientStatusError(statusResponse?.status)) {
        throw createDownloadError(statusResponse);
      }

      await retryStatus(requestStartedAt);
      continue;
    }

    if (
      !statusResponse.data ||
      !Object.values(JobStatus).includes(statusResponse.data.status)
    ) {
      await retryStatus(requestStartedAt);
      continue;
    }

    if (failureDeadline !== undefined) {
      onReconnecting?.(false);
    }

    failureDeadline = undefined;
    const { status, progressPercent } = statusResponse.data;

    // A responsive but stalled job must still expire. Only genuine forward
    // progress renews the packaging wait, not repeated or regressing values.
    if (
      Number.isFinite(progressPercent) &&
      progressPercent > highestProgressPercent &&
      progressPercent <= 100
    ) {
      highestProgressPercent = progressPercent;
      packagingDeadline = Date.now() + DOWNLOAD_JOB_CONFIG.PACKAGING_TIMEOUT_MS;
    }

    onProgress?.(progressPercent || 0);

    if (status === JobStatus.COMPLETE) {
      // The package is ready on the server, but the browser must acquire it
      // before the user can save it. Keep this separate from packaging progress.
      onAcquiring?.();
      checkPollingCancelled(signal);
      const blob = await mediator.request<Blob>('api:invoke', {
        method: 'GET',
        url: job.downloadUrl,
        responseType: 'blob',
      });

      checkPollingCancelled(signal);

      if (!(blob instanceof Blob)) {
        throw createDownloadError(blob as DownloadErrorResponse);
      }

      return {
        blob,
        checksumSha256: statusResponse.data.checksumSha256,
      };
    }

    if (status === JobStatus.FAILED) {
      console.error(
        'Download job failed:',
        job.jobKey,
        statusResponse.data.failureReason,
      );
      throw new Error(i18n.t('Modal_Download.error_generic_failed'));
    }

    const remainingMs = packagingDeadline - Date.now();

    if (remainingMs <= 0) {
      throw createDownloadError();
    }

    await waitForNextStatus(
      Math.min(DOWNLOAD_JOB_CONFIG.POLL_INTERVAL_MS, remainingMs),
      signal,
    );
  }
};

const handleDownloadData = async (
  options: DownloadDataOptions,
): Promise<DownloadPackageResult> => {
  const {
    createJobEndpoint,
    jobStatusEndpoint,
    downloadEndpoint,
    createJobPayload,
    onProgress,
    onReconnecting,
    signal,
    onJobCreated,
    onAcquiring,
  } = options;

  const jobResponse = await mediator.request<{ jobKey: string }>('api:invoke', {
    method: createJobEndpoint.Method,
    url: urls.api + createJobEndpoint.Path,
    data: createJobPayload,
  });

  if (jobResponse.status !== 202) {
    throw createDownloadError(jobResponse);
  }

  const { jobKey } = jobResponse.data;

  return pollDownloadJob(
    {
      jobKey,
      statusUrl: urls.api + jobStatusEndpoint.Path.replace(':jobKey', jobKey),
      downloadUrl: urls.api + downloadEndpoint.Path.replace(':jobKey', jobKey),
    },
    onProgress,
    onReconnecting,
    signal,
    onJobCreated,
    onAcquiring,
  );
};

const handleDownloadSelectedSubjectData = (
  request: MediatorRequestWithResponse<DownloadPackageResult>,
) => {
  const { subjectKeys } = request;

  if (!subjectKeys || subjectKeys.length === 0) {
    request.response = Promise.reject(
      new Error(i18n.t('Modal_Download.error_no_subjects')),
    );
    return;
  }

  request.response = handleDownloadData({
    createJobEndpoint: ApiEndpoints.EP_SUBJECT_CREATE_DOWNLOAD_JOB,
    jobStatusEndpoint: ApiEndpoints.EP_SUBJECT_GET_DOWNLOAD_JOB_STATUS,
    downloadEndpoint: ApiEndpoints.EP_SUBJECT_GET_DOWNLOAD_JOB,
    createJobPayload: {
      subjectKeys,
      outputFormat: DOWNLOAD_CONFIG.OUTPUT_FORMAT,
    },
    onProgress: request.onProgress,
    onReconnecting: request.onReconnecting,
    signal: request.signal,
    onJobCreated: request.onJobCreated,
    onAcquiring: request.onAcquiring,
  });
};

const handleDownloadStudyData = (
  request: MediatorRequestWithResponse<DownloadPackageResult>,
) => {
  request.response = handleDownloadData({
    createJobEndpoint: ApiEndpoints.EP_STUDY_CREATE_DOWNLOAD_JOB,
    jobStatusEndpoint: ApiEndpoints.EP_STUDY_GET_DOWNLOAD_JOB_STATUS,
    downloadEndpoint: ApiEndpoints.EP_STUDY_GET_DOWNLOAD_JOB,
    createJobPayload: {
      studyKey: request.studyKey,
      outputFormat: DOWNLOAD_CONFIG.OUTPUT_FORMAT,
    },
    onProgress: request.onProgress,
    onReconnecting: request.onReconnecting,
    signal: request.signal,
    onJobCreated: request.onJobCreated,
    onAcquiring: request.onAcquiring,
  });
};

mediator.subscribe('study:getStudies', handleGetStudiesByOrganization);
mediator.subscribe('study:getStudyByKey', handleGetStudyByKey);
mediator.subscribe('study:createStudy', handleCreateStudy);
mediator.subscribe('study:updateStudy', handleUpdateStudy);
mediator.subscribe('study:getStudyFullByKey', handleGetStudyFullByKey);
mediator.subscribe(
  'study:downloadSelectedSubjectData',
  handleDownloadSelectedSubjectData,
);
mediator.subscribe('study:downloadStudyData', handleDownloadStudyData);
